import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { projectDocumentsAPI } from '../../services/api';

const DocumentEditorTab = ({ projectId, projectSlug }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Active document type: 'terms' or 'privacy'
  const [activeDocType, setActiveDocType] = useState('terms');
  
  // Document contents
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  
  // Track which documents have changes
  const [termsChanged, setTermsChanged] = useState(false);
  const [privacyChanged, setPrivacyChanged] = useState(false);
  
  const [showPreview, setShowPreview] = useState(false);

  const messageTimeoutRef = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);

  useEffect(() => {
    loadDocuments();
  }, [projectId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Load terms
      try {
        const termsResponse = await projectDocumentsAPI.get(projectId, 'terms');
        if (termsResponse.data.success && termsResponse.data.document) {
          setTermsContent(termsResponse.data.document.content || '');
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error loading terms:', error);
        }
      }

      // Load privacy
      try {
        const privacyResponse = await projectDocumentsAPI.get(projectId, 'privacy');
        if (privacyResponse.data.success && privacyResponse.data.document) {
          setPrivacyContent(privacyResponse.data.document.content || '');
        }
      } catch (error) {
        if (error.response?.status !== 404) {
          console.error('Error loading privacy:', error);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docType = activeDocType;
      const content = docType === 'terms' ? termsContent : privacyContent;
      
      await projectDocumentsAPI.update(projectId, docType, { content });
      
      if (docType === 'terms') {
        setTermsChanged(false);
      } else {
        setPrivacyChanged(false);
      }
      
      messageTimeoutRef('success', `${docType === 'terms' ? 'Términos' : 'Política de Privacidad'} guardados exitosamente`);
    } catch (error) {
      console.error('Error saving document:', error);
      const errorMsg = error.response?.data?.error || 'Error al guardar el documento';
      messageTimeoutRef('error', errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    const url = activeDocType === 'terms' 
      ? `/terminos/${projectSlug}` 
      : `/privacidad/${projectSlug}`;
    window.open(url, '_blank');
  };

  const getCurrentContent = () => {
    return activeDocType === 'terms' ? termsContent : privacyContent;
  };

  const setCurrentContent = (value) => {
    if (activeDocType === 'terms') {
      setTermsContent(value);
      setTermsChanged(true);
    } else {
      setPrivacyContent(value);
      setPrivacyChanged(true);
    }
  };

  const hasChanges = activeDocType === 'terms' ? termsChanged : privacyChanged;
  const currentContent = getCurrentContent();
  const charCount = currentContent.length;

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Cargando...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold">Documentos Legales</h2>
          <p className="text-sm text-gray-500 mt-1">
            Personaliza los documentos legales para este proyecto
          </p>
        </div>
        <button
          type="button"
          onClick={handlePreview}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition"
        >
          Vista Previa
        </button>
      </div>

      {/* Warning Banner - Ley 29733 */}
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">
              Cumplimiento con Ley 29733 - Protección de Datos Personales
            </h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>
                Los documentos legales deben incluir el tratamiento de datos biométricos 
                (reconocimiento facial) cuando aplique. Asegúrate de que el contenido 
                cumpla con los requisitos de consentimiento informado.
              </p>
            </div>
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`mb-4 p-4 rounded ${
          message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      {/* Document Type Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveDocType('terms')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeDocType === 'terms'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Términos y Condiciones
          </button>
          <button
            onClick={() => setActiveDocType('privacy')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeDocType === 'privacy'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Política de Privacidad
          </button>
        </nav>
      </div>

      {/* Editor */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <label className="label mb-0">
            {activeDocType === 'terms' 
              ? 'Contenido de Términos y Condiciones' 
              : 'Contenido de Política de Privacidad'}
          </label>
          <span className="text-xs text-gray-500">
            {charCount.toLocaleString()} caracteres
          </span>
        </div>
        
        <textarea
          value={currentContent}
          onChange={(e) => setCurrentContent(e.target.value)}
          className="w-full h-80 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 font-mono text-sm resize-y"
          placeholder={`Ingresa el contenido de ${activeDocType === 'terms' ? 'términos y condiciones' : 'política de privacidad'} aquí...`}
        />
        
        <p className="text-xs text-gray-500">
          Este contenido se mostrará en las páginas de {activeDocType === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'} 
          {' '}para este proyecto. Si lo dejas vacío, se mostrará el contenido predeterminado.
        </p>
      </div>

      <div className="flex justify-end mt-6 pt-6 border-t">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Guardando...' : 'Guardar Documento'}
        </button>
      </div>
    </div>
  );
};

DocumentEditorTab.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  projectSlug: PropTypes.string.isRequired,
};

export default DocumentEditorTab;
